// Centralized fiscal rates (France) used across the app.
// Source of truth references (2026):
// - Cotisations micro-entrepreneur: Service-Public Entreprendre (F36232)
// - Versement libératoire: Service-Public Entreprendre (F23267)
// - ACRE: Service-Public Entreprendre (F11677)
//
// We keep these values centralized to avoid hard-coded rates scattered in the UI.

export const MICRO_URSSAF_RATES_PERCENT = Object.freeze({
  // Micro-social simplified regime (metropole)
  sales: 12.3,          // vente de marchandises + fourniture de logement
  services: 21.2,       // prestations de services (BIC)
  mixed: 21.2,          // fallback (mixed is often dominated by services)
  liberal_bnc: 25.6,    // libéral non réglementé (BNC)
  cipav: 23.2,          // libéral réglementé relevant de la CIPAV
  meuble: 21.2,         // location d'habitation meublée (Service-Public)
  tourisme: 6.0,        // meublé de tourisme (Service-Public)
});

export const VERSEMENT_LIBERATOIRE_RATES_PERCENT = Object.freeze({
  sales: 1.0,
  services: 1.7,
  bnc: 2.2,
});

/**
 * Default URSSAF rate based on activity_type.
 * Note: the app's UI mostly exposes sales/services/mixed. If the user explicitly
 * sets a custom rate, that remains the source of truth.
 */
export function getDefaultUrssafRatePercent(profileOrActivity = {}) {
  const t = String(profileOrActivity?.activity_type || 'services').toLowerCase();
  if (t in MICRO_URSSAF_RATES_PERCENT) return MICRO_URSSAF_RATES_PERCENT[t];
  if (t === 'sale' || t === 'ventes') return MICRO_URSSAF_RATES_PERCENT.sales;
  if (t === 'service' || t === 'prestations') return MICRO_URSSAF_RATES_PERCENT.services;
  return MICRO_URSSAF_RATES_PERCENT.services;
}

/**
 * Default versement libératoire rate based on activity_type.
 */
export function getDefaultVersementLiberatoireRatePercent(profileOrActivity = {}) {
  const t = String(profileOrActivity?.activity_type || 'services').toLowerCase();
  if (t === 'sales' || t === 'sale' || t === 'ventes') return VERSEMENT_LIBERATOIRE_RATES_PERCENT.sales;
  if (t === 'bnc' || t === 'liberal_bnc') return VERSEMENT_LIBERATOIRE_RATES_PERCENT.bnc;
  // services + mixed default to 1.7%
  return VERSEMENT_LIBERATOIRE_RATES_PERCENT.services;
}

/**
 * Returns effective URSSAF + income tax (VL only) rates, with safe defaults.
 */
export function getEffectiveRatesFromProfile(profile = {}) {
  const urssafRatePercent =
    typeof profile?.urssaf_rate_percent === 'number'
      ? profile.urssaf_rate_percent
      : getDefaultUrssafRatePercent(profile);

  const incomeTaxRatePercent = profile?.versement_liberatoire
    ? (typeof profile?.tax_rate_percent === 'number'
        ? profile.tax_rate_percent
        : getDefaultVersementLiberatoireRatePercent(profile))
    : 0;

  return { urssafRatePercent, incomeTaxRatePercent };
}

function endOfQuarterDate(year, quarter) {
  const endMonth = quarter * 3; // 3,6,9,12
  // Day 0 of next month gives last day of endMonth
  return new Date(year, endMonth, 0, 23, 59, 59, 999);
}

/**
 * ACRE (micro-entrepreneur): 50% reduction on social contributions
 * until the end of the 3rd calendar quarter following the start date.
 * If no valid start date is provided, we keep the base rate.
 */
export function applyAcreReductionPercent(baseUrssafRatePercent, { acre_enabled, acre_start_date } = {}) {
  const base = Number(baseUrssafRatePercent);
  if (!Number.isFinite(base)) return baseUrssafRatePercent;

  if (!acre_enabled) return base;
  if (!acre_start_date) return base;

  const start = new Date(acre_start_date);
  if (Number.isNaN(start.getTime())) return base;

  const startQuarter = Math.floor(start.getMonth() / 3) + 1; // 1..4
  const targetQuarterIndex = startQuarter + 3;
  const endYear = start.getFullYear() + Math.floor((targetQuarterIndex - 1) / 4);
  const endQuarter = ((targetQuarterIndex - 1) % 4) + 1;

  const end = endOfQuarterDate(endYear, endQuarter);
  const now = new Date();

  if (now <= end) return Number((base * 0.5).toFixed(2));
  return base;
}
